import React, { useRef, useEffect, useState } from 'react';
import tokens from '../../../../theme/tokens';

/**
 * Canvas-based gauge for LPF and SPI metrics.
 *
 * Low zone (0.5–1.0) and high zone (1.0–1.5), white separator at 1.0,
 * needle pointing to current value, colored value text in center.
 * No Syncfusion dependency.
 *
 * Props:
 *   value          — numeric metric value (e.g. 1.271)
 *   size           — pixel width of the canvas (default 170)
 *   goodDirection  — 'below' (default: low zone green) or 'above' (high zone
 *                    green), from the project's KPI orientation (ticket #264)
 */
const LaborGauge = ({ value, size = 170, goodDirection = 'below' }) => {
  const canvasRef = useRef(null);
  const [fontsReady, setFontsReady] = useState(false);

  // Canvas text does not re-render when a web font finishes loading, so redraw once Inter is ready.
  useEffect(() => {
    let active = true;
    document.fonts?.ready.then(() => active && setFontsReady(true));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = size;
    const H = Math.round(size * 0.82);
    const cx = W / 2;
    const cy = H - 14;
    const r = Math.round(W * 0.41);
    const lw = Math.round(r * 0.19);

    ctx.clearRect(0, 0, W, H);

    const SA = (2 * Math.PI) / 3;
    const EA = (7 * Math.PI) / 3;
    const TA = (5 * Math.PI) / 3;
    const MIN = 0.5;
    const MAX = 1.5;

    // Track background
    ctx.beginPath();
    ctx.arc(cx, cy, r, SA, EA);
    ctx.strokeStyle = '#D3D1C7';
    ctx.lineWidth = lw;
    ctx.lineCap = 'butt';
    ctx.stroke();

    const aboveIsGood = goodDirection === 'above';

    // Low zone (0.5 → 1.0): green unless above 1.0 is good
    const thA = SA + ((1.0 - MIN) / (MAX - MIN)) * TA;
    ctx.beginPath();
    ctx.arc(cx, cy, r, SA, thA);
    ctx.strokeStyle = aboveIsGood ? '#F0997B' : '#5DCAA5';
    ctx.lineWidth = lw;
    ctx.stroke();

    // High zone (1.0 → 1.5): red unless above 1.0 is good
    ctx.beginPath();
    ctx.arc(cx, cy, r, thA, EA);
    ctx.strokeStyle = aboveIsGood ? '#5DCAA5' : '#F0997B';
    ctx.lineWidth = lw;
    ctx.stroke();

    // White separator at 1.0
    ctx.beginPath();
    ctx.moveTo(
      cx + Math.cos(thA) * (r + lw / 2 - 1),
      cy + Math.sin(thA) * (r + lw / 2 - 1)
    );
    ctx.lineTo(
      cx + Math.cos(thA) * (r - lw / 2 + 1),
      cy + Math.sin(thA) * (r - lw / 2 + 1)
    );
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Tick labels
    ctx.font = `${Math.round(r * 0.125)}px ${tokens.fontFamily}`;
    ctx.fillStyle = '#888780';
    ctx.textAlign = 'center';
    [0.7, 1.0, 1.3].forEach((v) => {
      const a = SA + ((v - MIN) / (MAX - MIN)) * TA;
      const tx = cx + Math.cos(a) * (r - lw * 1.75);
      const ty = cy + Math.sin(a) * (r - lw * 1.75);
      if (ty < H - 4) ctx.fillText(v.toFixed(1), tx, ty + 4);
    });

    // Needle
    const safeVal = Number.isFinite(value) ? value : 1.0;
    const clampedVal = Math.min(Math.max(safeVal, MIN), MAX);
    const vA = SA + ((clampedVal - MIN) / (MAX - MIN)) * TA;
    const nx = cx + Math.cos(vA) * r * 0.73;
    const ny = cy + Math.sin(vA) * r * 0.73;
    const bx = cx + Math.cos(vA + Math.PI) * r * 0.11;
    const by = cy + Math.sin(vA + Math.PI) * r * 0.11;

    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(nx, ny);
    ctx.strokeStyle = '#2C2C2A';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.07, 0, 2 * Math.PI);
    ctx.fillStyle = '#2C2C2A';
    ctx.fill();

    // Value text
    const isGood = aboveIsGood ? safeVal >= 1.0 : safeVal <= 1.0;
    ctx.fillStyle = isGood ? '#0F6E56' : '#993C1D';
    ctx.font = `500 ${Math.round(r * 0.33)}px ${tokens.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText(safeVal.toFixed(3), cx, cy - r * 0.12);
  }, [value, size, goodDirection, fontsReady]);

  return (
    <canvas ref={canvasRef} width={size} height={Math.round(size * 0.82)} />
  );
};

export default LaborGauge;
